import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PhilosophySection = () => {
  const [activePhilosophy, setActivePhilosophy] = useState(0);

  const philosophies = [
    {
      id: 0,
      title: "Empowered Growth Through Code",
      subtitle: "Core Philosophy",
      description: "Technology becomes transformative when combined with mentorship and community impact. Code is not just about solving problems—it\'s about empowering others to solve their own.",
      principles: [
        {
          icon: "Users",
          title: "Community First",
          detail: "Every line of code should consider its impact on the community. Building inclusive solutions that lift others up."
        },
        {
          icon: "BookOpen",
          title: "Continuous Learning",
          detail: "The moment we stop learning is the moment we stop growing. Embrace curiosity and share knowledge freely."
        },
        {
          icon: "Heart",
          title: "Human-Centered Tech",
          detail: "Technology should serve humanity, not the other way around. Always consider the human element in every solution."
        }
      ],
      examples: [
        "CodersHub workshops designed for beginners with zero coding experience",
        "Mentorship programs that focus on both technical and soft skills",
        "Open-source contributions that prioritize documentation and accessibility"
      ]
    },
    {
      id: 1,
      title: "Bridge Building",
      subtitle: "Cultural Philosophy",
      description: "Creating connections between Cameroonian and African talent and global opportunities while preserving local context and values. Technology should unite, not divide.",
      principles: [
        {
          icon: "Globe",
          title: "Global Perspective",
          detail: "Understanding international standards while celebrating local innovation and creativity."
        },
        {
          icon: "Handshake",
          title: "Cultural Sensitivity",
          detail: "Respecting diverse backgrounds and creating inclusive environments where everyone can thrive."
        },
        {
          icon: "Zap",
          title: "Local Impact",
          detail: "Using global knowledge to solve local problems and create opportunities within our communities."
        }
      ],
      examples: [
        "Connecting Cameroonian developers with international remote opportunities",
        "Adapting global best practices to local business contexts",
        "Creating culturally relevant learning materials and examples"
      ]
    },
    {
      id: 2,
      title: "Sustainable Mentorship",
      subtitle: "Teaching Philosophy",
      description: "True mentorship creates more mentors. The goal is not just to teach skills, but to develop leaders who will teach others and multiply the impact.",
      principles: [
        {
          icon: "Target",
          title: "Goal-Oriented Learning",
          detail: "Every learning session should have clear objectives and measurable outcomes for both mentor and mentee."
        },
        {
          icon: "Repeat",
          title: "Iterative Growth",
          detail: "Learning is a continuous cycle of practice, feedback, and improvement. Embrace failures as learning opportunities."
        },
        {
          icon: "Award",
          title: "Recognition & Celebration",
          detail: "Acknowledging progress and celebrating achievements, no matter how small, builds confidence and motivation."
        }
      ],
      examples: [
        "Structured mentorship programs with clear milestones and feedback loops",
        "Peer-to-peer learning sessions where mentees become mentors",
        "Public recognition of mentee achievements to inspire others"
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-muted/50 to-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-1 bg-accent rounded-full"></div>
            <span className="text-accent font-medium tracking-wider uppercase text-sm">
              Philosophy
            </span>
            <div className="w-12 h-1 bg-accent rounded-full"></div>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-secondary mb-6">
            Guiding
            <span className="text-gradient-gold"> Principles</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            The beliefs and values that shape how I approach technology, mentorship, and community building.
          </p>
        </div>

        {/* Philosophy Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {philosophies?.map((philosophy, index) => (
            <button
              key={philosophy?.id}
              onClick={() => setActivePhilosophy(index)}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                activePhilosophy === index
                  ? 'bg-accent text-secondary shadow-lg transform scale-105'
                  : 'bg-white text-muted-foreground hover:bg-accent/10 hover:text-accent border border-border'
              }`}
            >
              {philosophy?.subtitle}
            </button>
          ))}
        </div>

        {/* Active Philosophy Content */}
        <div className="space-y-12 animate-fade-in">
          {/* Main Philosophy */}
          <div className="text-center space-y-6">
            <h3 className="text-2xl lg:text-4xl font-bold text-secondary">
              {philosophies?.[activePhilosophy]?.title}
            </h3>
            <p className="text-lg lg:text-xl text-foreground max-w-4xl mx-auto leading-relaxed">
              {philosophies?.[activePhilosophy]?.description}
            </p>
          </div>

          {/* Principles Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {philosophies?.[activePhilosophy]?.principles?.map((principle, index) => (
              <div key={index} className="card-elevated p-6 text-center space-y-4 hover:shadow-elevated transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center mx-auto shadow-lg">
                  <Icon name={principle?.icon} size={28} className="text-white" />
                </div>
                <h4 className="text-xl font-semibold text-secondary">
                  {principle?.title}
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  {principle?.detail}
                </p>
              </div>
            ))}
          </div>

          {/* Real Examples */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-elegant">
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="Lightbulb" size={24} className="text-accent" />
                  <h4 className="text-2xl font-bold text-secondary">Philosophy in Action</h4>
                </div>
                <p className="text-muted-foreground">
                  How these principles manifest in real-world projects and initiatives
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {philosophies?.[activePhilosophy]?.examples?.map((example, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg">
                    <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Check" size={14} className="text-white" />
                    </div>
                    <p className="text-foreground leading-relaxed">{example}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quote Section */}
          <div className="text-center py-12">
            <div className="max-w-4xl mx-auto">
              <Icon name="Quote" size={48} className="text-accent/30 mx-auto mb-6" />
              <blockquote className="text-2xl lg:text-3xl font-medium text-secondary leading-relaxed mb-6">
                "The best code is not the most clever or complex—it's the code that empowers others to build something meaningful."
              </blockquote>
              <div className="flex items-center justify-center space-x-4">
                <div className="w-12 h-1 bg-accent rounded-full"></div>
                <span className="text-muted-foreground font-medium">Adrian's Philosophy</span>
                <div className="w-12 h-1 bg-accent rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;